/**
 * Conversions between the ISO-8601 strings the API speaks and the `YYYY-MM-DDTHH:mm` local-time
 * format a `<input type="datetime-local">` accepts.
 *
 * Extracted because ShiftDetail.vue and ServiceJurisdictionDetail.vue had byte-identical copies under
 * different names. Both need it for the same structural reason: EntityForm.vue copies fetched API
 * values straight into the model's reactive state and PUTs that state back verbatim, so the model must
 * mirror the API shape and the input format has to be converted at the component edge.
 */

/**
 * ISO-8601 (or anything `Date` accepts) to the value a datetime-local input renders, in local time.
 *
 * Strips a trailing `[Zone]` suffix first: that is what Java's `ZonedDateTime.toString()` emits, and
 * `new Date()` returns Invalid Date for it, which would silently blank the field.
 *
 * @param {string|null|undefined} isoString
 * @returns {string} the input value, or '' when there is nothing valid to show
 */
export function toDatetimeLocal(isoString) {
  if (!isoString) return '';
  const date = new Date(String(isoString).replace(/\[[^\]]*\]$/, ''));
  if (isNaN(date)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * A datetime-local input value back to ISO-8601 with this browser's offset.
 *
 * `emptyValue` is what a cleared input becomes, and the two callers genuinely differ: a shift requires
 * its dates, so null ("absent") is right there; a jurisdiction promo window is clearable, and its
 * endpoint reads absent as "leave unchanged" and an explicit blank as "clear", so '' is right there.
 *
 * @param {string|null|undefined} localValue
 * @param {string|null} [emptyValue=null] what to return for a cleared or unparseable input
 * @returns {string|null}
 */
export function fromDatetimeLocal(localValue, emptyValue = null) {
  if (!localValue) return emptyValue;
  const date = new Date(localValue);
  return isNaN(date) ? emptyValue : date.toISOString();
}
